// Checkout protocol (Neel Slack DM 2026-10-19): admin-side reads of a
// tenant's move-out date request, for the Move-out tab's notification card
// and the confirmed-date card's provenance line.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type MoveOutDateRequestRow = {
  id: string;
  requested_date: string;
  created_at: string;
  status: "pending" | "approved" | "rejected";
};

/** The tenant's own PENDING request for this booking, or null — drives the
 *  Move-out tab's notification card and the header/tab badges. */
export function useMoveOutDateRequestForBooking(
  bookingId: number | null | undefined
) {
  return useQuery<MoveOutDateRequestRow | null>({
    queryKey: ["admin.move-out-request", bookingId],
    enabled: !!bookingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("move_out_date_requests")
        .select("id, requested_date, created_at, status")
        .eq("booking_id", bookingId!)
        .eq("status", "pending")
        .maybeSingle();
      if (error) throw error;
      return (data as MoveOutDateRequestRow | null) ?? null;
    },
  });
}

/**
 * The most recent request for this booking REGARDLESS of status — used only
 * for the "Requested by tenant on {date}" provenance line on the confirmed
 * "Move-out date" card, which must still show after the pending row above
 * has already flipped to `approved`.
 */
export function useLatestMoveOutDateRequestForBooking(
  bookingId: number | null | undefined
) {
  return useQuery<MoveOutDateRequestRow | null>({
    queryKey: ["admin.move-out-request.latest", bookingId],
    enabled: !!bookingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("move_out_date_requests")
        .select("id, requested_date, created_at, status")
        .eq("booking_id", bookingId!)
        .order("created_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      return (data?.[0] as MoveOutDateRequestRow | undefined) ?? null;
    },
  });
}
